import React, { useState, useEffect } from 'react';
import apiService from '../services/api.service';
import authService from '../services/auth.service';
import { useNavigate } from 'react-router-dom';
import './ImageGallery.css';
import { galleryCategories } from '../constants/galleryCategories';

const ImageGallery = () => {
  const navigate = useNavigate();
  
  // State variables
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [months, setMonths] = useState([]);
  const [days, setDays] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('Vitrin');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [monthCounts, setMonthCounts] = useState({});
  const [dayCounts, setDayCounts] = useState({});
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState(null);

  // Month names in Turkish
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Helper function to get days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Check authentication and initialize on component mount
  useEffect(() => {
    const checkAuthAndFetchData = async () => {
      // Check if user is logged in
      if (!authService.isLoggedIn()) {
        navigate('/admin/login');
        return;
      }
      
      // Initialize with current date
      initializeWithCurrentDate();
    };

    checkAuthAndFetchData();
  }, [navigate]);

  // Initialize calendar with current date
  const initializeWithCurrentDate = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    
    // Set selected year, month, day
    setSelectedYear(currentYear);
    
    // Generate months for the current year
    const monthsArray = Array.from({ length: 12 }, (_, i) => i + 1);
    setMonths(monthsArray);
    
    // Generate days for the current month
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDays(daysArray);
    
    // Set selected month and day
    setSelectedMonth(currentMonth);
    setSelectedDay(currentDay);
    
    // Fetch images for today
    fetchImagesForDay(currentYear, currentMonth, currentDay);
    
    // Fetch month and day counts
    fetchDaysData(currentYear, currentMonth);
  };

  // Select a month
  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    setImages([]);
    setError(null);
    setDeleteSuccess(false);
    
    // Generate days for the selected month
    const daysInMonth = getDaysInMonth(selectedYear, month);
    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    setDays(daysArray);
    
    // Fetch data for this month
    fetchDaysData(selectedYear, month);
  };

  // Select a day
  const handleDayClick = (day) => {
    setSelectedDay(day);
    setError(null);
    setUploadSuccess(false);
    setDeleteSuccess(false);
    
    // Fetch images for this day
    fetchImagesForDay(selectedYear, selectedMonth, day);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
      setError(null); // Clear any previous errors
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    setUploadCategory(e.target.value);
  };

  // Handle API errors and token expiration
  const handleApiError = (err, defaultMessage) => {
    console.error('API error:', err);
    
    // Check if unauthorized (token expired or invalid)
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      // Redirect to login
      navigate('/admin/login');
      return;
    }
    
    // Display error message
    setError(err.response?.data?.error || defaultMessage);
  };

  // Handle image delete
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Bu görseli silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    setDeletingImageId(imageId);
    setError(null);
    setDeleteSuccess(false);
    
    try {
      await apiService.delete(`/api/gallery/images/${imageId}`);
      
      // Success - remove image from local state
      setImages(images.filter(img => img.id !== imageId));
      setDeleteSuccess(true);
      
      // Refresh day count after deletion
      fetchDaysData(selectedYear, selectedMonth);
      
      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (err) {
      handleApiError(err, 'Görsel silinirken bir hata oluştu');
    } finally {
      setDeletingImageId(null);
    }
  };

  // Handle image upload
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadFile) {
      setError('Lütfen bir dosya seçin.');
      return;
    }
    
    if (!selectedYear || !selectedMonth || !selectedDay) {
      setError('Lütfen bir tarih seçin.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setUploadSuccess(false);
    setDeleteSuccess(false);
    
    try {
      // Create the form data
      const formData = new FormData();
      formData.append('image', uploadFile);
      formData.append('category', uploadCategory);
      formData.append('year', selectedYear.toString());
      formData.append('month', selectedMonth.toString());
      formData.append('day', selectedDay.toString());
      
      // apiService.post için özel headers
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };
      
      // Send the request
      const response = await apiService.post('/api/gallery/upload', formData, config);
      
      // Success
      console.log('Yükleme başarılı:', response.data);
      setUploadSuccess(true);
      
      // Refresh images after upload
      fetchImagesForDay(selectedYear, selectedMonth, selectedDay);
      
      // Also refresh the day counts
      fetchDaysData(selectedYear, selectedMonth);
      
      // Reset file input
      setUploadFile(null);
      document.getElementById('admin-file-upload').value = '';
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
    } catch (err) {
      handleApiError(err, 'Görsel yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch days data
  const fetchDaysData = async (year, month) => {
    setLoading(true);
    try {
      const response = await apiService.get(`/api/gallery/days?year=${year}&month=${month}`);
      const dayData = response.data;
      
      // Create a map of day -> count
      const countMap = {};
      dayData.forEach(item => {
        countMap[item.day] = item.count;
      });
      
      setDayCounts(countMap);
      setError(null);
    } catch (err) {
      // Don't set error to UI for this one - we'll still show all days
      console.error('Error fetching days:', err);
      handleApiError(err, null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch images for a specific day
  const fetchImagesForDay = async (year, month, day) => {
    setLoading(true);
    setImages([]);
    try {
      // Ensure month and day are padded with leading zeros if needed
      const paddedMonth = String(month).padStart(2, '0');
      const paddedDay = String(day).padStart(2, '0');
      
      const response = await apiService.get(`/api/gallery/images?year=${year}&month=${paddedMonth}&day=${paddedDay}`);
      setImages(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching images:', err);
      handleApiError(err, 'Görseller yüklenirken bir hata oluştu');
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Render calendar navigation (similar to employee component)
  const renderCalendar = () => {
    return (
      <div className="admin-calendar-container">
        <div className="admin-calendar-header">
          <h3>Tarih Seçin</h3>
          <div className="admin-date-display">
            <span className="admin-selected-date">
              {selectedDay} {monthNames[selectedMonth - 1]} {selectedYear}
            </span>
          </div>
        </div>
        
        {/* Month selector */}
        <div className="admin-months-selector">
          <div className="admin-selector-heading">Ay</div>
          <div className="admin-month-buttons">
            {months.map(month => (
              <button
                key={month}
                className={`admin-month-button ${selectedMonth === month ? 'active' : ''}`}
                onClick={() => handleMonthClick(month)}
              >
                {monthNames[month - 1].slice(0, 3)}
              </button>
            ))}
          </div>
        </div>
        
        {/* Day selector */}
        <div className="admin-days-selector">
          <div className="admin-selector-heading">Gün</div>
          <div className="admin-days-grid">
            {days.map(day => (
              <button
                key={day}
                className={`admin-day-button ${selectedDay === day ? 'active' : ''}`}
                onClick={() => handleDayClick(day)}
              >
                {day}
                {dayCounts[day] > 0 && <span className="admin-day-count">{dayCounts[day]}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render images view with upload form (similar to employee component but with delete functionality)
  const renderImagesAndUpload = () => {
    // Get API URL from environment or default
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
    
    return (
      <div className="admin-gallery-container">
        {/* Upload form */}
        <div className="admin-upload-form-container">
          <h3>Yeni Görsel Yükle</h3>
          <form onSubmit={handleUpload} className="admin-upload-form">
            <div className="admin-form-group">
              <label htmlFor="admin-category">Kategori:</label>
              <select 
                id="admin-category" 
                value={uploadCategory} 
                onChange={handleCategoryChange}
                className="admin-form-control"
              >
                {galleryCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group admin-file-group">
              <label htmlFor="admin-file-upload">Görsel Seç:</label>
              <div className="admin-file-input-container">
                <input 
                  type="file" 
                  id="admin-file-upload" 
                  accept="image/*"
                  onChange={handleFileChange} 
                  className="admin-file-input"
                />
                <button 
                  type="button" 
                  className="admin-file-button"
                  onClick={() => document.getElementById('admin-file-upload').click()}
                >
                  {uploadFile ? 'Görsel Seçildi' : 'Görsel Seç'}
                </button>
                {uploadFile && <span className="admin-file-name">{uploadFile.name}</span>}
              </div>
            </div>
            <div className="admin-form-group">
              <button 
                type="submit" 
                className="admin-upload-button"
                disabled={loading || !uploadFile}
              >
                {loading ? 'Yükleniyor...' : 'Yükle'}
              </button>
            </div>
          </form>
          
          {/* Error/Success messages */}
          {error && <div className="admin-error-message">{error}</div>}
          {uploadSuccess && <div className="admin-success-message">Görsel başarıyla yüklendi!</div>}
          {deleteSuccess && <div className="admin-success-message">Görsel başarıyla silindi!</div>}
        </div>
        
        {/* Image gallery */}
        <div className="admin-image-display">
          <h3>
            {selectedDay} {monthNames[selectedMonth - 1]} {selectedYear} - Yüklenen Görseller
            {loading && <span className="admin-loading-indicator"> (Yükleniyor...)</span>}
          </h3>
          
          <div className="admin-image-grid">
            {loading && images.length === 0 ? (
              <div className="admin-loading-message">Görseller yükleniyor...</div>
            ) : images.length > 0 ? (
              images.map(image => (
                <div key={image.id} className="admin-image-item">
                  {/* Image with correct URL */}
                  <img 
                    src={`${API_URL}${image.file_path}`} 
                    alt={image.category}
                    onError={(e) => {
                      console.error('Image failed to load:', image.file_path);
                      e.target.src = `${API_URL}/uploads/error.png`;
                      e.target.alt = 'Görsel yüklenemedi';
                    }}
                  />
                  <div className="admin-image-details">
                    <span className="admin-image-category">{image.category}</span>
                    <span className="admin-image-uploader">
                      {image.uploader_name || image.employee_name || 'Admin'}
                    </span>
                    <span className="admin-image-date">
                      {image.createdAt
                        ? new Date(image.createdAt).toLocaleString('tr-TR')
                        : new Date(image.upload_date).toLocaleDateString('tr-TR')}
                    </span>
                    
                    {/* Delete button - Admin only feature */}
                    <button 
                      className="admin-delete-button"
                      onClick={() => handleDeleteImage(image.id)}
                      disabled={deletingImageId === image.id}
                      title="Görseli Sil"
                    >
                      {deletingImageId === image.id ? 'Siliniyor...' : 'Sil'}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-no-images">Bu tarih için yüklenmiş görsel bulunmamaktadır.</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Main render function
  return (
    <div className="admin-image-gallery-component">
      <div className="admin-section-header">
        <h2>Görsel Yönetimi (Admin)</h2>
      </div>
      
      <div className="admin-content-layout">
        {renderCalendar()}
        {renderImagesAndUpload()}
      </div>
    </div>
  );
};

export default ImageGallery;
